import { useMemo } from "react";
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory
} from "react-router-dom";
import { Flow } from "../../../../shared/components/Flow";
import { StepWhatToExpect } from "./StepWhatToExpect";
import { StepConfirmCancellation } from "./StepConfirmCancellation";
import {
  getPathnameDetailsFromEnrollment,
  getPathnameMyResourcesSubmitCancellation,
  PARAMETER_MY_RESOURCES_PLAN_ENROLLMENT_ID,
  PATH_MY_RESOURCES
} from "../../paths";
import { usePlanEnrollment } from "../../../../query/benefits/usePlanEnrollment";
import { WSQueries } from "../../../../query/WSQuery";
import { toWSDateString } from "@wingspanhq/fe-component-library";
import { selectorPlanNameByPlanType } from "../../selectors/selectorPlanNameByPlanType";

export const RouteSubmitCancellationFlow: React.FC<
  RouteComponentProps<
    {
      [PARAMETER_MY_RESOURCES_PLAN_ENROLLMENT_ID]: string;
    },
    any,
    {
      backPath?: string;
    }
  >
> = props => {
  const history = useHistory();
  const qPlanEnrollment = usePlanEnrollment(
    props.match.params.planEnrollmentId
  );

  const backPath = useMemo(
    () => props.location.state?.backPath || PATH_MY_RESOURCES,
    []
  );

  return (
    <WSQueries queries={{ qPlanEnrollment }}>
      {({ qPlanEnrollmentData }) => {
        const planType = qPlanEnrollmentData.planType;
        const planTitle = selectorPlanNameByPlanType(planType);

        const basePath = getPathnameMyResourcesSubmitCancellation(
          qPlanEnrollmentData.planEnrollmentId
        );
        const isCanceled = qPlanEnrollmentData.status === "Terminated";
        const date = qPlanEnrollmentData.endDate;

        return (
          <Switch>
            <Route
              path={basePath}
              render={props => (
                <Flow
                  title="Submit cancellation"
                  basePath={basePath}
                  steps={[
                    {
                      slug: "submit",
                      title: "Submit cancellation",
                      steps: [
                        {
                          slug: "what-to-expect",
                          title: "What to expect",
                          component: props => (
                            <StepWhatToExpect
                              planType={planType}
                              enrollmentName={planTitle}
                              {...props}
                            />
                          )
                        },
                        {
                          slug: "confirm",
                          title: "Confirm & cancel",
                          component: props => (
                            <StepConfirmCancellation
                              planType={planType}
                              planEnrollmentId={
                                qPlanEnrollmentData.planEnrollmentId
                              }
                              {...props}
                            />
                          )
                        }
                      ],
                      status: isCanceled ? "Complete" : undefined,
                      result: {
                        Complete: {
                          description: (
                            <>
                              Your cancellation to <b>{planTitle}</b> was
                              submitted and your coverage will end on{" "}
                              {toWSDateString(date)}.
                            </>
                          ),
                          title: "Your cancellation was submitted"
                        }
                      }
                    }
                  ]}
                  hideNavigation={isCanceled}
                  onBack={() => {
                    history.push(backPath);
                  }}
                  onNext={() => {
                    history.push(
                      getPathnameDetailsFromEnrollment(
                        qPlanEnrollmentData.planEnrollmentId
                      )
                    );
                  }}
                />
              )}
            />

            <Redirect from="*" to={basePath} />
          </Switch>
        );
      }}
    </WSQueries>
  );
};
